import { Stack, useRouter, useSegments } from 'expo-router';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useEffect, useMemo } from 'react';
import { X } from 'lucide-react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useOnboarding } from '@/store/onboarding';

// Location is collected on the welcome screen now (name + address + ZIP
// + climate all in one), so it's no longer a separate step in the flow.
//
// `lifestyle` is real — property.tsx routes to it, and lifestyle.tsx
// branches internally to `detail` (full) or `generating` (quick). If it
// were missing from these arrays the progress bar would snap back to 0%
// on the lifestyle screen.
const SCREENS_QUICK: readonly string[] = [
  'welcome',
  'path',
  'home-type',
  'year',
  'size',
  'hvac',
  'inside',
  'property',
  'lifestyle',
  'generating',
  'plan',
  'notify',
];

const SCREENS_FULL: readonly string[] = [
  'welcome',
  'path',
  'home-type',
  'year',
  'size',
  'hvac',
  'inside',
  'property',
  'lifestyle',
  'detail',
  'generating',
  'plan',
  'notify',
];

// Blank Start — minimal 3-step pipeline. We still hit `generating` and
// `plan` so the property commits + the user lands on My Home; we just
// skip every chip-pick screen between path and generating. Progress
// bar tops out fast because it's a 4-step ride.
const SCREENS_BLANK: readonly string[] = [
  'welcome',
  'path',
  'generating',
  'plan',
  'notify',
];

const QUESTION_SCREENS = new Set([
  'home-type',
  'year',
  'size',
  'hvac',
  'inside',
  'property',
  'lifestyle',
  'detail',
]);

// `notify` hides back because the property has already committed by
// then — stepping back to the plan screen would re-run the commit.
const HIDDEN_BACK = new Set(['welcome', 'generating', 'plan', 'notify']);

export default function OnboardingLayout() {
  const router = useRouter();
  const segments = useSegments();
  const mode = useOnboarding((s) => s.mode);
  // /add-home sets this flag so we know we're walking the SECOND/Nth home
  // through onboarding (vs the very first install). When true, every
  // screen needs a visible exit so the user can bail back to My Home
  // mid-flow without losing their existing home(s).
  const isAddingHome = useOnboarding((s) => s.isAddingHome);

  const order =
    mode === 'full' ? SCREENS_FULL : mode === 'blank' ? SCREENS_BLANK : SCREENS_QUICK;
  const current = segments[segments.length - 1] as string;
  const stepIndex = Math.max(0, order.indexOf(current));
  const progress = order.length > 1 ? stepIndex / (order.length - 1) : 0;

  const fill = useSharedValue(progress);
  useEffect(() => {
    fill.value = withTiming(progress, { duration: 420 });
  }, [progress, fill]);

  const fillStyle = useAnimatedStyle(() => ({
    width: `${fill.value * 100}%`,
  }));

  const questionCount = useMemo(
    () => order.filter((s) => QUESTION_SCREENS.has(s)).length,
    [order]
  );
  const questionIndex = QUESTION_SCREENS.has(current)
    ? order.filter((s) => QUESTION_SCREENS.has(s)).indexOf(current) + 1
    : 0;

  const showBack = !HIDDEN_BACK.has(current) && stepIndex > 0;
  // In add-home mode the user always needs an exit — on the welcome
  // step where there's nothing to go back to, we render the X (close)
  // in the same slot so they can bail back to /(tabs)/my-home without
  // losing their existing homes. Initial-install onboarding still hides
  // the slot entirely (HIDDEN_BACK), because there's no home to return
  // to and an exit there would just dump them mid-setup.
  const showAddHomeExit = isAddingHome && !showBack;

  const onExitAddHome = () => {
    // Clear the flag so we don't ghost-show the X on a future flow.
    useOnboarding.setState({ isAddingHome: false });
    router.replace('/(tabs)/my-home' as never);
  };

  return (
    <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
      <View style={styles.topbar}>
        {showAddHomeExit ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close — back to My Home"
            onPress={onExitAddHome}
            style={styles.back}
            hitSlop={12}>
            <X size={18} color={Palette.ink} strokeWidth={2} />
          </Pressable>
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Back"
            onPress={() => router.back()}
            style={[styles.back, !showBack && styles.backHidden]}
            hitSlop={12}>
            <Text style={styles.backArrow}>‹</Text>
          </Pressable>
        )}
        <View style={styles.progress}>
          <Animated.View style={[styles.progressFill, fillStyle]} />
        </View>
        {/* In add-home mode, every step gets a top-right X exit so the
            user can bail back to My Home without losing their other
            homes — back-only-on-each-step left them stuck stepping
            backward through 8 screens to escape. Step counter still
            renders to the right of it on steps where back is the
            primary action. */}
        {isAddingHome && showBack ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close — back to My Home"
            onPress={onExitAddHome}
            style={styles.exitBtn}
            hitSlop={10}>
            <X size={16} color={Palette.inkSoft} strokeWidth={2} />
          </Pressable>
        ) : null}
        <Text style={styles.stepNum}>
          {questionIndex > 0 ? `${questionIndex} / ${questionCount}` : ''}
        </Text>
      </View>

      <Stack
        screenOptions={{
          headerShown: false,
          animation: 'slide_from_right',
          contentStyle: { backgroundColor: Palette.sand },
        }}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: Palette.sand,
  },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.three,
    paddingHorizontal: Spacing.five,
    paddingTop: Spacing.two,
    height: 44,
  },
  // Compact secondary X (right side, add-home mode only). Smaller and
  // less-loud than the back-arrow button so it reads as an escape
  // hatch, not the primary action.
  exitBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  back: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  backArrow: {
    fontSize: 22,
    lineHeight: 24,
    color: Palette.ink,
    fontFamily: Fonts.sansSemibold,
    marginTop: -3,
  },
  progress: {
    flex: 1,
    height: 6,
    borderRadius: Radius.pill,
    backgroundColor: Palette.sandDeep,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: Radius.pill,
    backgroundColor: Palette.forest,
  },
  stepNum: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkFaint,
    minWidth: 46,
    textAlign: 'right',
    letterSpacing: 0.4,
  },
});
